import React, {useState} from "react";
import {twJoin} from "tailwind-merge";
import {Badge, defaultBorderColor} from "peggirkit";
import {Product} from "@/data/entities/menu";
import {productPlaceholderImgSrc} from "@/data/entities/product";
import {FormattedMessage} from "react-intl";
import BadgeDotIndicator from "peggirkit/dist/components/Badge/BadgeElements/Indicator/BadgeDotIndicator";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import ProductPrice from "@widgets/pos/order/menu/products/price/ProductPrice";
import useMenu from "@widgets/pos/order/hooks/MenuProvider";

type Props = {
    product: Product,
    isPending: boolean,
};

const ProductButton = ({isPending, product}: Props) => {
    const {showProductInfo} = useMenu();
    const fulfillmentType = useFulfillmentType();
    const [loadingImage, setLoadingImage] = useState(true);
    const [imgSrc, setImgSrc] = useState(product.images.length > 0 ? product.images[0] : productPlaceholderImgSrc);

    return (
        <button
            className={twJoin(
                "w-full h-full p-3 rounded-lg text-left flex flex-col gap-3 items-start justify-start text-sm",
                "bg-white dark:bg-slate-800 shadow-sm transition-all hover:ring-1 ring-blue-600",
                defaultBorderColor,
            )}
            disabled={isPending}
            onClick={(e) => {
                e.preventDefault();
                showProductInfo({product});
            }}
        >
            <div className={"w-full flex flex-row items-start justify-start gap-3"}>
                <img
                    className={twJoin(
                        "object-cover rounded-lg shrink-0 grow-0 w-[120px] h-[120px] bg-eat-yellow-400/10",
                        loadingImage ? "animate-pulse" : "",
                        product.soldout ? "opacity-70" : "",
                    )}
                    src={imgSrc}
                    onError={() => {
                        setLoadingImage(false);
                        setImgSrc(productPlaceholderImgSrc)
                    }}
                    onLoad={() => setLoadingImage(false)}
                    alt={""}
                    width={120}
                    height={120}
                />

                <div className={"w-full flex flex-col"}>
                    <h3 className={"font-bold text-lg"}>
                        {product.name}
                    </h3>
                    <p className={"opacity-75 text-[95%]"}>
                        {product.description}
                    </p>
                </div>
            </div>

            <div className={"w-full flex flex-col gap-3"}>
                <div className={"w-full flex flex-row items-center justify-start gap-1"}>
                    {
                        product.soldout
                            ? (
                                <Badge color={"danger"}>
                                    <FormattedMessage id={"soldOut"}/>
                                </Badge>
                            )
                            : (
                                <Badge color={"success"}>
                                    <FormattedMessage id={"inStock"}/>
                                </Badge>
                            )
                    }

                    <span className={"opacity-30"}>•</span>

                    {product.availability.map((availability: string) =>
                        <Badge
                            key={availability}
                            color={"success"}
                            indicator={
                                availability.toUpperCase() === fulfillmentType
                                    ? <BadgeDotIndicator/>
                                    : undefined
                            }
                        >
                            <FormattedMessage id={availability}/>
                        </Badge>
                    )}
                </div>

                <ProductPrice product={product}/>
            </div>
        </button>
    );
};

export default ProductButton;